import React, { useEffect } from 'react';
import routes from '../../../../../Routes/routes';
import { getUrlParameters, TAB_INDEX } from "../../External/Model/Model";
import { REQUEST_ACCESS_STEP_MODEL } from "../Model/Model";

/**
 * FindMySelfMainApp
 *
 * Component to handle the “Find Myself” flow.
 * Manages step navigation, button enable/disable logic, and triggers request creation.
 *
 * @author Karen Yunuen
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Function} navigate - Function to navigate programmatically (react-router).
 * @param {Object} steps - Object representing the steps of the QR request flow.
 * @param {Function} setSteps - Function to update the steps state.
 * @param {number} activeStep - Current active step index.
 * @param {Array} visitorList - List of selected visitors.
 * @param {Object} requestAccessItem - Request access object with details and validations.
 * @param {Function} setBtnDisabled - Function to enable/disable the Next button.
 * @param {Function} handleCreate - Function to create the request access.
 * @param {Function} navigateStep - Function to navigate to a specific step.
 * @param {Function} resetData - Function to reset all form and state data.
 *
 * @returns {Object} An object containing handler functions:
 * - handleClickNext: Function to navigate to the next step or trigger create request.
 * - handleNavigateBack: Function to navigate to the previous step or reset data.
 */
const FindMySelfMainApp = (
    {
        navigate,
        steps,
        setSteps,
        activeStep,
        visitorList,
        requestAccessItem,
        setBtnDisabled,
        handleCreate,
        navigateStep,
        resetData
    }
) => {

    /**
     * Set initial steps when component mounts.
     *
     * @dependencies [] - Runs only once on mount.
     */
    useEffect(() => {
        setSteps({ ...REQUEST_ACCESS_STEP_MODEL.FIND_MYSELF });
    }, []);

    /**
     * Enable or disable the Next button based on current step and form validation.
     *
     * @dependencies [steps, activeStep, visitorList, requestAccessItem?.requestReason]
     */
    useEffect(() => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                if (visitorList.length === 0) setBtnDisabled(true);
                else setBtnDisabled(false);
                break;
            case TAB_INDEX.TWO:
                if (!requestAccessItem?.isValid()) setBtnDisabled(true);
                else setBtnDisabled(false);
                break;
            default:
                break;
        }
    }, [steps, activeStep, visitorList, requestAccessItem?.requestReason]);

    /**
     * Handle the Next button click and navigate to the appropriate step.
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => navigateStep(TAB_INDEX.TWO), // Start flow with visitor selected (from ONE step)
            [TAB_INDEX.TWO]: () => navigateStep(TAB_INDEX.THREE), // Start flow without visitor selected (from TWO step)
            [TAB_INDEX.THREE]: () => handleCreate(TAB_INDEX.FOUR),
            [TAB_INDEX.FOUR]: () => navigate(getUrlParameters({ url: routes.qrRequestAccess.main }))
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    /**
     * Handle the Back button click and navigate to the previous step or reset data.
     */
    const handleNavigateBack = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => navigate(getUrlParameters({ url: routes.qrRequestAccess.main })),
            [TAB_INDEX.TWO]: () => {
                navigateStep(TAB_INDEX.ONE);
                resetData();
            },
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.FOUR]: () => navigateStep(TAB_INDEX.THREE)
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    return {
        handleClickNext,
        handleNavigateBack
    };
};

export default FindMySelfMainApp;
